import React, { useEffect } from 'react';
import { useEmpresaLayout } from '@/hooks/useEmpresaLayout';

const DEFAULT_PRIMARY = '#34343A';
const DEFAULT_ACCENT = '#7E7E86';

const hexToHsl = (hexValue?: string | null): string | null => {
  if (!hexValue) return null;

  const normalized = hexValue.replace('#', '').trim();
  if (!/^[0-9a-fA-F]{6}$/.test(normalized)) return null;

  const r = parseInt(normalized.slice(0, 2), 16) / 255;
  const g = parseInt(normalized.slice(2, 4), 16) / 255;
  const b = parseInt(normalized.slice(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      default:
        h = (r - g) / d + 4;
    }

    h /= 6;
  }

  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
};

const setCssVariable = (name: string, value: string | null) => {
  if (!value) return;
  document.documentElement.style.setProperty(name, value);
};

const AppThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { layoutConfig } = useEmpresaLayout();

  useEffect(() => {
    const primary = hexToHsl(layoutConfig?.cor_primaria) || hexToHsl(DEFAULT_PRIMARY);
    const accent = hexToHsl(layoutConfig?.cor_accent || layoutConfig?.cor_secundaria) || hexToHsl(DEFAULT_ACCENT);

    setCssVariable('--primary', primary);
    setCssVariable('--ring', primary);
    setCssVariable('--accent', accent);
    setCssVariable('--accent-foreground', hexToHsl(DEFAULT_PRIMARY));
  }, [layoutConfig?.cor_accent, layoutConfig?.cor_primaria, layoutConfig?.cor_secundaria]);

  return <>{children}</>;
};

export default AppThemeProvider;
